var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Collapsible, css, Tabs, useDebounce } from '@showroomjs/ui';
import * as React from 'react';
import { useCodeTheme } from '../lib/code-theme-context';
import { useCodeBlocks } from '../lib/codeblocks-context';
import { useHighlights } from '../lib/use-highlights';
import { PreviewConsoleProvider } from '../lib/use-preview-console';
import { useTargetAudience } from '../lib/use-target-audience';
import { A11yResultPanel } from './a11y-result-panel';
import { A11ySummary } from './a11y-summary';
import { Div } from './base';
import { CodeEditor } from './code-editor';
import { CodePreviewFrame } from './code-preview-frame';
import { CodePreviewIframe, } from './code-preview-iframe';
import { CodePreviewShowroomFrame } from './code-preview-showroom-frame';
import { ConsolePanel } from './console-panel';
import { LinkToStandaloneView } from './link-to-standalone-view';
import { MeasuringButton } from './measuring-button';
export const CodeLiveEditor = (_a) => {
    var { hasHeading, className, noEditor, lang, frame = lang === 'html', initialHeight, height, initialWidth } = _a, props = __rest(_a, ["hasHeading", "className", "noEditor", "lang", "frame", "initialHeight", "height", "initialWidth"]);
    const theme = useCodeTheme();
    const [code, setCode] = React.useState(props.code);
    const debouncedCode = useDebounce(code);
    const [showDetails, setShowDetails] = React.useState(false);
    const codeBlocks = useCodeBlocks();
    const matchedCodeData = codeBlocks[props.code];
    const targetAudience = useTargetAudience();
    const isDeveloper = targetAudience === 'developer';
    const initialHeightValue = initialHeight && Number(initialHeight);
    const heightValue = height && Number(height);
    const initialWidthValue = initialWidth && Number(initialWidth);
    const frameRef = React.useRef(null);
    const [isMeasuring, setIsMeasuring] = React.useState(false);
    const highlightCurrentFrameItems = useHighlights();
    const highlightFrameItems = React.useCallback((selectors, newColor) => {
        if (frame) {
            if (frameRef.current) {
                frameRef.current.sendToChild({
                    type: 'highlightElements',
                    selectors,
                    color: newColor,
                });
            }
        }
        else {
            highlightCurrentFrameItems(selectors, newColor);
        }
    }, [highlightCurrentFrameItems]);
    const [activeTab, setActiveTab] = React.useState('code');
    const a11yPanelOnly = noEditor || !isDeveloper;
    return (React.createElement("div", { className: className },
        React.createElement(PreviewConsoleProvider, null,
            React.createElement(Div, { css: Object.assign({ position: 'relative', roundedT: hasHeading ? '$none' : '$base' }, (frame
                    ? {
                        backgroundColor: '$gray-400',
                        borderBottomRightRadius: '$base',
                        width: '100%',
                    }
                    : {
                        minHeight: 48,
                        border: '1px solid',
                        borderColor: '$gray-300',
                    })) }, frame ? (React.createElement(CodePreviewIframe, { code: debouncedCode, lang: lang, codeHash: matchedCodeData && matchedCodeData.initialCodeHash, initialHeight: initialHeightValue && !isNaN(initialHeightValue)
                    ? initialHeightValue
                    : undefined, height: heightValue && !isNaN(heightValue) ? heightValue : undefined, initialWidth: initialWidthValue && !isNaN(initialWidthValue)
                    ? initialWidthValue
                    : undefined, resizable: true, imperativeRef: frameRef })) : (React.createElement(CodePreviewShowroomFrame, { code: debouncedCode, lang: lang }))),
            React.createElement(ConsolePanel, null),
            React.createElement(Collapsible.Root, { open: showDetails, onOpenChange: setShowDetails },
                React.createElement(Div, { css: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: '$1',
                    } },
                    React.createElement(Collapsible.Button, { css: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '$1',
                            fontSize: '$sm',
                            lineHeight: '$sm',
                        } },
                        React.createElement(Collapsible.ToggleIcon, { hide: showDetails, "aria-label": showDetails ? 'Hide' : 'View', width: "16", height: "16" }),
                        a11yPanelOnly ? 'Accessibility' : 'Details'),
                    React.createElement(Div, { css: {
                            display: 'inline-flex',
                            gap: '$1',
                        } },
                        React.createElement(A11ySummary, { onClick: () => {
                                setShowDetails(true);
                                if (!a11yPanelOnly) {
                                    setActiveTab('a11y');
                                }
                            }, css: {
                                px: '$2',
                                borderRight: frame || !noEditor ? '1px solid $gray-300' : undefined,
                            } }),
                        frame && (React.createElement(MeasuringButton, { onClick: () => {
                                if (frameRef.current) {
                                    const nextIsMeasuring = !isMeasuring;
                                    frameRef.current.sendToChild({
                                        type: 'toggleMeasure',
                                        active: nextIsMeasuring,
                                    });
                                    setIsMeasuring(nextIsMeasuring);
                                }
                            }, isActive: isMeasuring })),
                        !noEditor && (React.createElement(LinkToStandaloneView, { codeHash: matchedCodeData && matchedCodeData.initialCodeHash, isDesigner: targetAudience === 'designer' })))),
                React.createElement(Collapsible.Content, { animate: true }, a11yPanelOnly ? (React.createElement(Div, { css: {
                        backgroundColor: '$gray-200',
                    } },
                    React.createElement(A11yResultPanel, { highlightItems: highlightFrameItems }))) : (React.createElement(Tabs.Root, { value: activeTab, onValueChange: (tab) => {
                        setActiveTab(tab);
                        if (tab === 'code') {
                            highlightFrameItems([], '');
                        }
                    } },
                    React.createElement(Tabs.List, null,
                        React.createElement(Tabs.Trigger, { value: "code" }, "Code"),
                        React.createElement(Tabs.Trigger, { value: "a11y" }, "Accessibility")),
                    React.createElement(Tabs.Content, { value: "code" },
                        React.createElement(CodeEditor, { code: code, onChange: setCode, language: lang, className: editorBottom(), theme: theme })),
                    React.createElement(Tabs.Content, { value: "a11y" },
                        React.createElement(A11yResultPanel, { highlightItems: highlightFrameItems })))))))));
};
export const NonVisualCodeLiveEditor = (props) => {
    const theme = useCodeTheme();
    const [code, setCode] = React.useState(props.code);
    const debouncedCode = useDebounce(code);
    const codeBlocks = useCodeBlocks();
    const matchedCodeData = codeBlocks[props.code];
    const [isCompiling, setIsCompiling] = React.useState(false);
    return (React.createElement("div", { className: props.className, id: props.id },
        React.createElement(PreviewConsoleProvider, null,
            props.noEditor ? null : (React.createElement(CodeEditor, { code: code, onChange: setCode, language: props.lang, className: editorBottom(), theme: theme })),
            React.createElement(Div, { css: {
                    position: 'relative',
                } }, props.frame ? (React.createElement(CodePreviewIframe, { code: debouncedCode, lang: props.lang, codeHash: matchedCodeData && matchedCodeData.initialCodeHash, nonVisual: true, onIsCompilingChange: setIsCompiling })) : (React.createElement(CodePreviewFrame, { code: debouncedCode, lang: props.lang, nonVisual: true, onIsCompilingChange: setIsCompiling }))),
            React.createElement(ConsolePanel, { defaultIsOpen: true, isCompiling: isCompiling }))));
};
const editorBottom = css({
    borderRadius: '$base',
});
//# sourceMappingURL=code-live-editor.js.map